import { createContext, useContext, useEffect, useRef, useState } from "react";

// Global polling interval shared across all widgets. 0 = off.
// Pages/widgets register a callback via useAutoRefresh; when the interval
// fires, every registered callback is invoked.

interface AutoRefreshCtx {
  intervalMs: number;
  setIntervalMs: (ms: number) => void;
  tick: number; // increments each interval; pages can watch it
  forceRefresh: () => void; // manual bump (e.g. clicking the active nav link)
}

const Ctx = createContext<AutoRefreshCtx>({
  intervalMs: 0,
  setIntervalMs: () => {},
  tick: 0,
  forceRefresh: () => {},
});

export function AutoRefreshProvider({ children }: { children: React.ReactNode }) {
  const [intervalMs, setIntervalMsRaw] = useState<number>(() => {
    const stored = parseInt(localStorage.getItem("autoRefreshMs") ?? "0", 10);
    return Number.isFinite(stored) && stored >= 0 ? stored : 0;
  });
  const [tick, setTick] = useState(0);
  const timerRef = useRef<number | null>(null);

  const setIntervalMs = (ms: number) => {
    setIntervalMsRaw(ms);
    localStorage.setItem("autoRefreshMs", String(ms));
  };

  useEffect(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (intervalMs > 0) {
      timerRef.current = window.setInterval(() => {
        setTick((t) => t + 1);
      }, intervalMs);
    }
    return () => {
      if (timerRef.current !== null) clearInterval(timerRef.current);
    };
  }, [intervalMs]);

  const forceRefresh = () => setTick((t) => t + 1);

  return (
    <Ctx.Provider value={{ intervalMs, setIntervalMs, tick, forceRefresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAutoRefresh() {
  return useContext(Ctx);
}

// Hook for any data-loading widget: returns a "version" number that
// increments on every refresh tick. Page bumps it locally for manual refresh.
export function useRefreshKey(): [number, () => void] {
  const { tick } = useAutoRefresh();
  const [manual, setManual] = useState(0);
  return [tick + manual * 1000, () => setManual((m) => m + 1)];
}
